import type { SourceRestaurantData } from "../../../../../stores/appSourceRestaurantDataStore";
import React from "react";
import { useTranslation } from "react-i18next";
import { primaryText, secondaryText, greyTextColor, textBlue, borderPrimary } from "../../../../../styling/stylingHelper";
import { useSubrecipesStore } from "../../../../../stores/appSubrecipesStore";
import { getPortionsUnitDisplay } from "../../../../../utils/ingredientGrouping";

interface SubrecipeDetailViewProps {
    subrecipeId: string;
    dataOverride?: SourceRestaurantData;
    onBack: () => void;
    onClose: () => void;
}

const SubrecipeDetailView: React.FC<SubrecipeDetailViewProps> = ({ subrecipeId, onBack, onClose, dataOverride }) => {
    const { t } = useTranslation();
    const liveSubrecipes = useSubrecipesStore(s => s.subrecipesById);
    const subrecipe = (dataOverride?.subrecipes ?? liveSubrecipes).get(subrecipeId);
    const subrecipesById = dataOverride?.subrecipes ?? liveSubrecipes;

    return (
        <>
            {/* Desktop header */}
            <div className="hidden md:flex items-center justify-between px-5 py-4 border-b border-[#E8E8E9] dark:border-borderPrimary-dark">
                <button
                    type="button"
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-borderPrimary-dark"
                    onClick={onBack}
                >
                    <img src="/assets/arrow-narrow-left.svg" alt="back" />
                </button>
                <button
                    type="button"
                    onClick={onClose}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-borderPrimary-dark"
                >
                    <img src="/assets/close-modal.svg" alt="close" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 sm:px-5 pb-5 pt-5 md:pt-4">
                {/* Mobile header */}
                <div className="flex items-center gap-4 md:hidden mb-5">
                    <button type="button" onClick={onBack}>
                        <img src="/assets/arrow-narrow-left.svg" alt="arrow-narrow-left" />
                    </button>
                    <span className="text-[24px] leading-9 font-semibold tracking-[-0.24px] text-[#181624] dark:text-textPrimary-dark">
                        {subrecipe?.name ?? t('costPrice.subrecipes.subrecipeTag')}
                    </span>
                </div>

                <h2 className="text-[28px] leading-10 tracking-[-0.28px] font-bold text-[#161924] dark:text-textPrimary-dark mb-6">
                    {subrecipe?.name ?? t('costPrice.subrecipes.deletedSubrecipeLabel')}
                </h2>

                {subrecipe && (
                    <p className={`${greyTextColor} text-[13px] mb-3`}>
                        {t('costPrice.subrecipes.yieldLabel', { portions: subrecipe.portions, unit: getPortionsUnitDisplay(subrecipe.portionsUnit) })}
                    </p>
                )}

                <div className="space-y-3">
                    {subrecipe?.ingredients.map((ingredient) => {
                        // Nested-subrecipe row: render as a single summary row (no drill-in here).
                        if (ingredient.nestedSubrecipeId) {
                            const child = subrecipesById.get(ingredient.nestedSubrecipeId);
                            return (
                                <div key={ingredient.id} className={`rounded-lg border ${borderPrimary} bg-backgroundSecondary dark:bg-backgroundSecondary-dark p-4`}>
                                    <div className="flex justify-between items-start mb-2">
                                        <p className={`text-[16px] leading-6 font-semibold ${primaryText}`}>
                                            {child?.name ?? t('costPrice.subrecipes.deletedSubrecipeLabel')}
                                            <span className={`ml-2 text-[12px] font-normal ${greyTextColor}`}>{t('costPrice.subrecipes.subrecipeTag')}</span>
                                        </p>
                                    </div>
                                    <div className="flex justify-between items-center gap-2">
                                        <p className={`text-[14px] leading-[21px] font-normal ${greyTextColor}`}>{t('costPrice.subrecipes.quantityLabel')}</p>
                                        <p className={`text-[14px] leading-5 font-medium ${secondaryText}`}>
                                            {ingredient.nestedSubrecipePortionsNeeded ?? 0} {getPortionsUnitDisplay(child?.portionsUnit ?? 'p')}
                                        </p>
                                    </div>
                                </div>
                            );
                        }

                        // Normal-ingredient row
                        const qtyNeeded = ingredient.quantityNeeded ?? 0;
                        const qtyInfo = ingredient.quantityInfo;
                        return (
                            <div key={ingredient.id} className={`rounded-lg border ${borderPrimary} bg-backgroundSecondary dark:bg-backgroundSecondary-dark p-4`}>
                                <div className="flex justify-between items-start mb-2">
                                    <p className={`text-[16px] leading-6 font-semibold ${primaryText}`}>
                                        {ingredient.productName}
                                    </p>
                                </div>
                                <div className="flex justify-between items-center gap-2">
                                    <p className={`text-[14px] leading-[21px] font-normal ${greyTextColor}`}>{t('costPrice.subrecipes.quantityLabel')}</p>
                                    <p className={`text-[14px] leading-5 font-medium ${secondaryText}`}>
                                        {qtyNeeded} {qtyInfo?.stdUnit ?? ''}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <p className={`mt-6 ${textBlue}`} />
            </div>
        </>
    );
};

export default SubrecipeDetailView;
